/*
  License validation widget used by license-gated product forms
  (support pack, additional activations, code upgrade, DSS variants).

  Exposed on window.SPH:
    LicenseValidator — validates a license code against the support API,
                       gates the ATC form on a successful response.
    QuantityRange   — small controller that wires +/- buttons inside the
                       theme's quantity-selector to a min/max range.

  Element contract for LicenseValidator (scoped to the form root):
    [data-license-code]        — text input where the user types the code
    [data-license-check]       — Check button
    [data-license-summary]     — element where messages render
    [data-activation-code-id]  — hidden input that ships to cart as a
                                 line-item property
    [data-license-atc]         — wrapper around ATC button + payment_button
                                 (hidden until validation succeeds)
    [data-license-spinner]     — element toggled with `visibility: visible`
                                 while a request is in flight
    [data-variants]            — variant <select> (Pixel Union convention);
                                 optional, falls back to fallbackSku
*/

(function (global) {
  'use strict';

  var SPH = global.SPH || (global.SPH = {});

  function LicenseValidator(root, config) {
    if (!root) throw new Error('LicenseValidator: root element is required');
    if (!config || !config.endpoint) {
      throw new Error('LicenseValidator: config.endpoint is required');
    }

    this.root = root;
    this.input = root.querySelector('[data-license-code]');
    this.button = root.querySelector('[data-license-check]');
    this.summary = root.querySelector('[data-license-summary]');
    this.activationIdField = root.querySelector('[data-activation-code-id]');
    this.atcWrapper = root.querySelector('[data-license-atc]');
    this.spinner = root.querySelector('[data-license-spinner]');
    this.variantSelect = root.querySelector('[data-variants]');

    this.endpoint = config.endpoint;
    this.skuField = config.skuField || 'sku_code';
    this.fallbackSku = config.fallbackSku || '';
    this.mailToEmail = config.mailToEmail || 'support@ugcs.com';
    this.successColor = config.successColor || '';

    this.onSuccess = typeof config.onSuccess === 'function' ? config.onSuccess : noop;
    this.onReset = typeof config.onReset === 'function' ? config.onReset : noop;

    this.bind();
  }

  LicenseValidator.prototype.bind = function () {
    var self = this;

    if (this.input) {
      this.input.addEventListener('input', function () { self.reset(); });
      this.input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (self.button) self.button.click();
        }
      });
    }

    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', function () { self.reset(); });
    }

    if (this.button) {
      this.button.addEventListener('click', function (event) {
        event.preventDefault();
        self.check();
      });
    }
  };

  LicenseValidator.prototype.reset = function () {
    if (this.atcWrapper) this.atcWrapper.style.display = 'none';
    if (this.activationIdField) this.activationIdField.value = '';
    if (this.summary) {
      this.summary.textContent = '';
      this.summary.style.color = '';
    }
    this.onReset();
  };

  LicenseValidator.prototype.check = function () {
    var self = this;
    if (this._inFlight) return Promise.resolve();

    var code = (this.input && this.input.value || '').trim();
    if (!code) {
      this.showError('Please enter a license code.');
      return Promise.resolve();
    }

    this._inFlight = true;
    this.setBusy(true);

    var body = { activation_code: code };
    body[this.skuField] = this.selectedSku();

    return fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.error_code) {
          self.showError(data.error_message);
          return;
        }
        if (!data || !data.activation_code_id) {
          throw new Error('Server unavailable');
        }

        if (self.activationIdField) self.activationIdField.value = data.activation_code_id;
        if (self.summary) self.summary.style.color = self.successColor;
        self.onSuccess(data);
        if (self.atcWrapper) self.atcWrapper.style.display = 'block';
      })
      .catch(function (err) {
        console.warn('[LicenseValidator]', err);
        self.showError("Can't reach external server. Please contact [mailToSupport].");
      })
      .then(function () {
        self._inFlight = false;
        self.setBusy(false);
      });
  };

  LicenseValidator.prototype.selectedSku = function () {
    var sel = this.variantSelect;
    if (sel && sel.selectedOptions && sel.selectedOptions[0]) {
      var opt = sel.selectedOptions[0];
      if (opt.dataset && opt.dataset.sku) return opt.dataset.sku;
    }
    return this.fallbackSku;
  };

  /*
    setSummaryText is the safe default — use it for any message that
    interpolates values from the API response. Renders as plain text.
  */
  LicenseValidator.prototype.setSummaryText = function (text) {
    if (!this.summary) return;
    this.summary.style.color = this.successColor;
    this.summary.textContent = text == null ? '' : String(text);
  };

  /*
    setSummaryHTML is the escape hatch for callers that need to render
    rich markup (headings, lists, line breaks). DO NOT pass in API
    response fields without escaping them first — this writes innerHTML
    and will execute any <script> or event handlers it contains.

    If you find yourself wanting to interpolate `data.something` from
    the response, use setSummaryText instead, or build a DocumentFragment
    by hand.
  */
  LicenseValidator.prototype.setSummaryHTML = function (html) {
    if (!this.summary) return;
    this.summary.style.color = this.successColor;
    this.summary.innerHTML = html == null ? '' : String(html);
  };

  /*
    showError renders untrusted API-supplied messages safely. The
    `[mailToSupport]` placeholder is the one spot where we inject HTML,
    and we do it by building a real <a> node — never via innerHTML on
    the API string.
  */
  LicenseValidator.prototype.showError = function (message) {
    if (!this.summary) return;
    this.summary.style.color = 'red';
    this.summary.textContent = '';

    var template = String(message || '');
    var placeholder = '[mailToSupport]';
    var idx = template.indexOf(placeholder);

    if (idx === -1) {
      this.summary.textContent = template;
      return;
    }

    this.summary.appendChild(document.createTextNode(template.slice(0, idx)));
    var link = document.createElement('a');
    link.className = 'support-link';
    link.href = 'mailto:' + this.mailToEmail;
    link.textContent = this.mailToEmail;
    this.summary.appendChild(link);
    this.summary.appendChild(document.createTextNode(template.slice(idx + placeholder.length)));
  };

  LicenseValidator.prototype.setBusy = function (busy) {
    var cls = 'disabled-element';
    if (this.button) this.button.classList.toggle(cls, !!busy);
    if (this.input) this.input.classList.toggle(cls, !!busy);
    if (this.spinner) this.spinner.style.visibility = busy ? 'visible' : 'hidden';
  };

  /*
    Wires +/- buttons inside the theme's quantity-selector to a
    bounded range. `span` is a shortcut for `max = min + span` so
    callers can say "min from API, allow 3 more".
  */
  function QuantityRange(root, options) {
    options = options || {};
    this.root = root;
    this.input = root.querySelector('.quantity-selector__input');
    this.minusBtn = root.querySelector('.quantity-selector__button--minus');
    this.plusBtn = root.querySelector('.quantity-selector__button--plus');
    this.minusWrap = root.querySelector('.quantity-selector__button-wrapper--minus');
    this.plusWrap = root.querySelector('.quantity-selector__button-wrapper--plus');

    this.min = typeof options.min === 'number' ? options.min : 1;
    if (typeof options.max === 'number') {
      this.max = options.max;
    } else if (typeof options.span === 'number') {
      this.max = this.min + options.span;
    } else {
      this.max = null;
    }
    this.current = this.min;
    this.onChange = typeof options.onChange === 'function' ? options.onChange : noop;

    if (this.input) this.input.value = this.current;
    this.refreshButtonState();

    var self = this;
    if (this.minusBtn) this.minusBtn.addEventListener('click', function () { self.set(self.current - 1); });
    if (this.plusBtn) this.plusBtn.addEventListener('click', function () { self.set(self.current + 1); });
  }

  QuantityRange.prototype.set = function (value) {
    if (this.max != null && value > this.max) return;
    if (value < this.min) return;
    this.current = value;
    if (this.input) this.input.value = value;
    this.refreshButtonState();
    this.onChange(this.current);
  };

  QuantityRange.prototype.refreshButtonState = function () {
    var disabledClass = 'quantity-selector__button-wrapper--disabled';
    if (this.minusWrap) this.minusWrap.classList.toggle(disabledClass, this.current <= this.min);
    if (this.plusWrap) {
      var atMax = this.max != null && this.current >= this.max;
      this.plusWrap.classList.toggle(disabledClass, atMax);
    }
  };

  function noop() {}

  SPH.LicenseValidator = LicenseValidator;
  SPH.QuantityRange = QuantityRange;
})(window);
